// reproductionTimelineLabels.js — تعريب مفردات الخطّ الزمنيّ في عيادة الإنتاج (3.6.2).
//
// مولَّد من frontend-Vit2/src/features/clinic/reproductionTimelineLabels.ts بـ
// scripts/sync_timeline_labels.js — لا تُحرّره هنا.
//
// الخادم يرسل الخطّ الزمنيّ بمفاتيحه الخام (`type` و`status`، وفي بعض الأحداث `title_ar`
// نسخةٌ من مفتاح إنجليزيّ كنوع الفحص)، وكانت الواجهة تعرضها كما هي: شارات «Insemination»
// و«Pending» و«Foaling Check» وسط نصّ عربيّ. الترجمة هنا لا في الخادم، لأنّ المفتاح هو ما
// يُخزَّن ويُقارَن ويُصفّى عليه — وتعريبه في القاعدة يكسر ذلك كلّه.
//
// المفردات كلّها منسوخة من تعريفات ENUM في
// `backend-node22/migrations/2026-05-13_reproduction_clinic_operational_module.sql`
// ومن `utils/reproductionTimelineBuilder.js` — لا من التخمين. وما لا يُعرف يُعرض كما هو:
// عرض مفتاح غريب أهون من حجب معلومة.

/** أنواع أحداث الخطّ الزمنيّ — من buildCaseTimeline. */
export const TIMELINE_TYPES = {
  Case: { ar: 'الحالة', en: 'Case' },
  'Expected Foaling': { ar: 'ولادة متوقّعة', en: 'Expected foaling' },
  Examination: { ar: 'فحص', en: 'Examination' },
  'Ovulation Trigger': { ar: 'تحفيز إباضة', en: 'Ovulation trigger' },
  'Ovulation Confirmation': { ar: 'تأكيد إباضة', en: 'Ovulation confirmation' },
  Insemination: { ar: 'تلقيح', en: 'Insemination' },
  'Pregnancy Check': { ar: 'فحص حمل', en: 'Pregnancy check' },
  'Follow-up': { ar: 'متابعة', en: 'Follow-up' },
  Scheduled: { ar: 'مجدول', en: 'Scheduled' },
  Task: { ar: 'مهمّة', en: 'Task' },
};

/** الحالات — تجمع حالات الحالة والمهامّ والتقويم والفحوص ونوافذ التوقيت. */
export const TIMELINE_STATUSES = {
  // حالة الحالة
  Open: { ar: 'مفتوحة', en: 'Open' },
  'Waiting Ovulation': { ar: 'بانتظار الإباضة', en: 'Waiting ovulation' },
  Ovulated: { ar: 'تمّت الإباضة', en: 'Ovulated' },
  Inseminated: { ar: 'ملقحة', en: 'Inseminated' },
  Flushed: { ar: 'تم غسل الأجنة', en: 'Flushed' },
  Transferred: { ar: 'تم النقل', en: 'Transferred' },
  Closed: { ar: 'مغلقة', en: 'Closed' },
  // المهامّ وأحداث التقويم
  Scheduled: { ar: 'مجدول', en: 'Scheduled' },
  Pending: { ar: 'قيد الانتظار', en: 'Pending' },
  'In Progress': { ar: 'قيد التنفيذ', en: 'In progress' },
  Completed: { ar: 'منجَز', en: 'Completed' },
  Overdue: { ar: 'متأخّر', en: 'Overdue' },
  Missed: { ar: 'فائت', en: 'Missed' },
  Cancelled: { ar: 'ملغى', en: 'Cancelled' },
  Done: { ar: 'تمّ', en: 'Done' },
  Skipped: { ar: 'متجاوَز', en: 'Skipped' },
  'Needs Action': { ar: 'يحتاج إجراءً', en: 'Needs action' },
  Emergency: { ar: 'طارئ', en: 'Emergency' },
  // نوافذ التوقيت (التلقيح والغسل)
  'Within Window': { ar: 'داخل النافذة', en: 'Within window' },
  Warning: { ar: 'تحذير', en: 'Warning' },
  'Outside Window': { ar: 'خارج النافذة', en: 'Outside window' },
  Override: { ar: 'تجاوز بقرار الطبيب', en: 'Overridden' },
  // نتائج فحص الحمل
  Pregnant: { ar: 'حامل', en: 'Pregnant' },
  'Not Pregnant': { ar: 'غير حامل', en: 'Not pregnant' },
  'Twin Pregnancy': { ar: 'حمل توأميّ', en: 'Twin pregnancy' },
  'Pregnancy Loss': { ar: 'فقد الحمل', en: 'Pregnancy loss' },
  Unclear: { ar: 'غير واضح', en: 'Unclear' },
};

/** مفاتيح تظهر داخل العناوين: أنواع الفحوص والسائل والمهامّ وأحداث التقويم. */
export const TIMELINE_TITLE_TOKENS = {
  // أنواع الفحوصات
  Initial: { ar: 'فحص أوّليّ', en: 'Initial examination' },
  Ultrasound: { ar: 'سونار', en: 'Ultrasound' },
  'Pregnancy Check': { ar: 'فحص حمل', en: 'Pregnancy check' },
  'Post Breeding': { ar: 'ما بعد التلقيح', en: 'Post breeding' },
  'Recipient Sync': { ar: 'تزامن المستقبِلة', en: 'Recipient synchronisation' },
  'Foaling Check': { ar: 'فحص الولادة', en: 'Foaling check' },
  'General Follow-up': { ar: 'متابعة عامّة', en: 'General follow-up' },
  // عناوين التلقيح كما يبنيها الخادم: `${semen_type} insemination` / `تلقيح ${semen_type}`.
  // تُسجَّل كاملةً لأنّ استبدال المفتاح وحده داخلها يُنتج خليطًا («طازج insemination»).
  'Fresh insemination': { ar: 'تلقيح بسائل طازج', en: 'Fresh insemination' },
  'Cooled insemination': { ar: 'تلقيح بسائل مبرَّد', en: 'Cooled insemination' },
  'Chilled insemination': { ar: 'تلقيح بسائل مبرَّد', en: 'Chilled insemination' },
  'Frozen insemination': { ar: 'تلقيح بسائل مجمَّد', en: 'Frozen insemination' },
  // أنواع السائل المنويّ
  Fresh: { ar: 'طازج', en: 'Fresh' },
  Cooled: { ar: 'مبرَّد', en: 'Cooled' },
  Chilled: { ar: 'مبرَّد', en: 'Chilled' },
  Frozen: { ar: 'مجمَّد', en: 'Frozen' },
  // أنواع المهامّ وأحداث التقويم
  Medical: { ar: 'طبّيّ', en: 'Medical' },
  Medication: { ar: 'دواء', en: 'Medication' },
  'Client Communication': { ar: 'تواصل مع العميل', en: 'Client communication' },
  'Lab Result': { ar: 'نتيجة مختبر', en: 'Lab result' },
  Vaccination: { ar: 'تطعيم', en: 'Vaccination' },
  Foaling: { ar: 'ولادة', en: 'Foaling' },
  'Foaling Monitoring': { ar: 'متابعة الولادة', en: 'Foaling monitoring' },
  'Embryo Flush': { ar: 'غسل أجنّة', en: 'Embryo flush' },
  'Embryo Transfer': { ar: 'نقل جنين', en: 'Embryo transfer' },
  Administrative: { ar: 'إداريّ', en: 'Administrative' },
  Other: { ar: 'أخرى', en: 'Other' },
};

/** ملاحظات آليّة يكتبها الخادم كعلامات لا كجُمل. */
export const TIMELINE_NOTES = {
  'protocol:auto-scheduled': { ar: 'مجدول تلقائيًّا حسب البروتوكول', en: 'Auto-scheduled by protocol' },
};

function pick(pair, lang, fallback) {
  if (!pair) return fallback;
  return lang === 'ar' ? pair.ar : pair.en;
}

export function timelineTypeLabel(type, lang) {
  return pick(TIMELINE_TYPES[type], lang, type || '-');
}

export function timelineStatusLabel(status, lang) {
  return pick(TIMELINE_STATUSES[status], lang, status || '');
}

export function timelineNote(note, lang) {
  return pick(TIMELINE_NOTES[note], lang, note || '');
}

/**
 * يعرّب عنوان الحدث. العنوان قد يكون مفتاحًا صرفًا ('Foaling Check') أو جملةً مركّبة فيها
 * مفتاح ('تلقيح Fresh')، فيُجرَّب التطابق الكامل أوّلًا ثمّ تُستبدَل المفاتيح المعروفة داخل
 * النصّ — الأطول أوّلًا كي لا يبتلع مفتاحٌ أقصرُ جزءًا من أطول منه. وما بقي من النصّ (اسم
 * دواء مثلًا) لا يُمسّ: هو بيانات المستخدم لا مفردات النظام.
 */
const WORD_CHAR = /[\p{L}\p{N}]/u;

function replaceToken(text, key, value) {
  // بحث نصّيّ لا نمطيّ: المفاتيح قيَم ENUM ثابتة، وبناء نمط منها يستدعي تهريبًا لا داعي له.
  let out = '';
  let index = 0;
  while (index < text.length) {
    const found = text.indexOf(key, index);
    if (found === -1) {
      out += text.slice(index);
      break;
    }
    const before = found === 0 ? '' : text[found - 1];
    const after = text[found + key.length] || '';
    const isolated = !WORD_CHAR.test(before) && !WORD_CHAR.test(after);
    out += text.slice(index, found) + (isolated ? value : key);
    index = found + key.length;
  }
  return out;
}

export function timelineTitle(title, lang) {
  const text = String(title || '').trim();
  if (!text) return '';
  const exact = TIMELINE_TITLE_TOKENS[text];
  if (exact) return pick(exact, lang, text);

  const keys = Object.keys(TIMELINE_TITLE_TOKENS).sort((a, b) => b.length - a.length);
  let result = text;
  for (const key of keys) {
    result = replaceToken(result, key, pick(TIMELINE_TITLE_TOKENS[key], lang, key));
  }
  return result;
}
